/**
 * Landing page
 *
 * FIX 3: Hero CTAs are auth-aware:
 *   - Unauthenticated → /signup
 *   - Authenticated   → /books/new
 *
 * FIX 7: CTAs match mockup-v3 spec:
 *   - Primary CTA: "✨ Generate your first chapter free →"
 *   - Secondary CTA: "Watch 2-min demo" → scrolls to #how-it-works
 *   - Mid-page CTA after features: "Start writing for free →"
 *   - Trust text: "No credit card required · Free chapter preview instantly"
 */
import { Link, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { HowItWorksDemo } from '../components/marketing/HowItWorksDemo';
import { DashboardDemo } from '../components/marketing/DashboardDemo';
import { KdpWalkthroughDemo } from '../components/marketing/KdpWalkthroughDemo';
import { useAuth } from '../hooks/useAuth';

export function Landing() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Auth-aware CTA destination
  const primaryCtaDest = isAuthenticated ? '/books/new' : '/signup';

  const handlePrimaryCta = () => {
    void navigate(primaryCtaDest);
  };

  const handleWatchDemo = () => {
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar variant="public" />

      {/* ── Hero ── */}
      <section className="max-w-4xl mx-auto text-center px-4 sm:px-6 py-16 sm:py-24">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-[#a78bfa] text-sm font-medium mb-6">
          🔥 Join 2,400+ authors who published this week
        </div>
        <h1 className="text-[clamp(2.25rem,6vw,3.75rem)] font-extrabold text-foreground mb-6 leading-[1.15] tracking-tight">
          Turn your story idea into a
          <br />
          <span className="text-primary">publishable novel in minutes</span>
        </h1>
        <p className="text-base sm:text-xl text-muted-foreground mb-8 sm:mb-10 max-w-2xl mx-auto leading-[1.65]">
          Write nothing. Generate everything. Earn royalties on autopilot. No writing experience needed.
        </p>
        <div className="flex flex-wrap gap-3 justify-center mb-4">
          <button
            onClick={handlePrimaryCta}
            className="px-8 py-3 bg-primary text-primary-foreground font-semibold rounded-md hover:bg-primary/90 transition-colors text-lg"
          >
            ✨ Generate your first chapter free →
          </button>
          <button
            onClick={handleWatchDemo}
            className="px-8 py-3 border border-border text-foreground font-semibold rounded-md hover:bg-accent transition-colors text-lg"
          >
            Watch 2-min demo
          </button>
        </div>
        <p className="text-sm text-muted-foreground">No credit card required · Free chapter preview instantly</p>

        {/* Social proof */}
        <p className="text-sm text-muted-foreground mt-4">
          ⭐⭐⭐⭐⭐{' '}
          <span className="italic">&ldquo;Published my first book on KDP in 48 hours&rdquo; - Sarah M.</span>
        </p>
      </section>

      {/* ── How It Works ── */}
      <section id="how-it-works" className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-3">How It Works</h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            From idea to published novel in three simple steps.
          </p>
        </div>

        <HowItWorksDemo />

        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {[
            {
              icon: '💡',
              step: 'Step 01',
              title: 'Enter Your Premise',
              desc: 'Describe your story idea in 1–3 sentences. Choose genre & tone. A short paragraph is all it takes - the more detail you give, the richer your novel will be.',
            },
            {
              icon: '⚡',
              step: 'Step 02',
              title: 'AI Writes Your Book',
              desc: '20 coherent chapters, 40K+ words, AI-generated cover art - in minutes. Every scene remembers your characters, plot threads, and world details automatically.',
            },
            {
              icon: '💰',
              step: 'Step 03',
              title: 'Publish on KDP',
              desc: 'Step-by-step KDP walkthrough. EPUB + DOCX export ready to upload. Your finished novel is formatted and ready to sell on Amazon in minutes.',
            },
          ].map((s) => (
            <div key={s.step} className="flex flex-col items-center text-center p-8 rounded-xl border border-border bg-card">
              <div className="text-[2.5rem] mb-3">{s.icon}</div>
              <div className="text-xs font-bold text-primary tracking-[0.12em] uppercase mb-2">{s.step}</div>
              <h3 className="text-lg font-semibold text-foreground mb-3 leading-snug">{s.title}</h3>
              <p className="text-muted-foreground text-sm leading-[1.65]">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-10 sm:pb-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-3">Everything you need to write your novel</h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            StoryMint handles the hard parts so you can focus on the story that matters to you.
          </p>
        </div>

        <DashboardDemo />

        <div className="grid md:grid-cols-3 gap-5 mb-12">
          {[
            {
              icon: '🧠',
              title: 'Story Memory',
              desc: 'Remembers every character, plot twist, and world detail from chapter 1 while writing chapter 20. Your story stays consistent all the way through.',
            },
            {
              icon: '📖',
              title: 'Multi-Chapter Generation',
              desc: 'Full novels from 30,000 to 80,000 words. Not short snippets - complete, publication-ready books written scene by scene, chapter by chapter.',
            },
            {
              icon: '📦',
              title: 'KDP-Ready Export',
              desc: 'Export your finished novel as EPUB or DOCX with proper formatting. Includes a step-by-step Amazon Kindle publishing walkthrough.',
            },
            {
              icon: '🎭',
              title: 'Custom Author Personas',
              desc: 'Create and save distinct writing voices - literary, pulp, slow burn romance, fast thriller. Your AI co-author writes in your chosen voice consistently.',
            },
            {
              icon: '🎁',
              title: 'Free to Start',
              desc: 'Get 3 full chapter previews on us - no credit card required. Fall in love with your story before you spend a penny.',
            },
            {
              icon: '✍️',
              title: 'Your Story, Your Rights',
              desc: 'Every word is yours. StoryMint never claims ownership of your work. Publish under your name and keep 100% of your royalties.',
            },
          ].map((f) => (
            <div
              key={f.title}
              className="p-6 border border-border rounded-xl bg-card hover:border-primary/35 transition-colors"
            >
              <div className="text-[1.875rem] mb-3">{f.icon}</div>
              <h3 className="text-base font-semibold text-foreground mb-2">{f.title}</h3>
              <p className="text-muted-foreground text-sm leading-[1.65]">{f.desc}</p>
            </div>
          ))}
        </div>

        <KdpWalkthroughDemo />

        {/* Mid-page CTA - matches mockup "Start writing for free →" */}
        <div className="text-center py-8">
          <button
            onClick={handlePrimaryCta}
            className="px-8 py-3 bg-primary text-primary-foreground font-semibold rounded-md hover:bg-primary/90 transition-colors text-lg"
          >
            Start writing for free →
          </button>
          <p className="text-xs text-muted-foreground mt-2">No credit card required · Free chapter preview instantly</p>
        </div>
      </section>

      {/* ── Stats / Social Proof ── */}
      <section className="border-t border-b border-border bg-card py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-xs font-semibold tracking-[0.1em] uppercase text-primary mb-10">
            Built for fiction writers
          </p>
          <div className="grid sm:grid-cols-3 gap-10">
            {[
              {
                stat: '12,400',
                accent: '+',
                label: 'Novels written by indie authors on StoryMint',
              },
              {
                stat: '4.8',
                accent: '★',
                label: 'Average author satisfaction score across all plans',
              },
              {
                stat: '83',
                accent: '%',
                label: 'Of free-tier users published their first book within 30 days of upgrading',
              },
            ].map((s) => (
              <div key={s.stat}>
                <h3 className="text-[clamp(2rem,5vw,3rem)] font-extrabold text-foreground tracking-tight mb-2">
                  {s.stat}
                  <span className="text-primary">{s.accent}</span>
                </h3>
                <p className="text-sm text-muted-foreground leading-[1.5]">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing Preview Strip ── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-foreground mb-3">Plans that grow with you</h2>
          <p className="text-muted-foreground">Start free - upgrade when you&apos;re ready to publish</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            {
              name: 'Free', price: '$0', period: '', credits: '3 chapter previews lifetime', popular: false,
              features: ['3 free chapter previews', 'No credit card required', 'All genres supported'],
            },
            {
              name: 'Starter', price: '$19', period: '/mo', credits: '2 books/month', popular: false,
              features: ['2 full novels per month', 'EPUB + DOCX export', 'Cover generation (3 variations)', 'KDP publishing walkthrough', 'Email support'],
            },
            {
              name: 'Creator', price: '$49', period: '/mo', credits: '5 books/month', popular: true,
              features: ['5 full novels per month', 'Everything in Starter', 'Series management', 'Author persona generator', 'Most Creative mode', 'Priority generation'],
            },
            {
              name: 'Publisher', price: '$99', period: '/mo', credits: '15 books/month', popular: false,
              features: ['15 full novels per month', 'Everything in Creator', 'Bulk generation queue', 'Priority support', 'Early access to new features'],
            },
          ].map((tier) => (
            <div
              key={tier.name}
              className={`relative flex flex-col p-6 rounded-xl bg-card border ${
                tier.popular ? 'border-primary shadow-[0_0_0_1px_hsl(var(--primary))]' : 'border-border'
              }`}
            >
              {tier.popular && (
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-semibold px-3 py-0.5 rounded-full whitespace-nowrap">
                  Most Popular
                </span>
              )}
              <div className="text-base font-bold text-foreground mb-1">{tier.name}</div>
              <div className="text-2xl font-extrabold text-foreground tracking-tight mb-1">
                {tier.price}
                <span className="text-sm font-normal text-muted-foreground">{tier.period}</span>
              </div>
              <p className="text-xs text-primary font-medium mb-3">{tier.credits}</p>
              <ul className="space-y-1.5 mt-auto">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs text-muted-foreground">
                    <span className="text-primary mt-px shrink-0">✓</span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Link
            to="/pricing"
            className="text-sm font-semibold text-[#a78bfa] hover:text-foreground transition-colors"
          >
            See full pricing &amp; feature comparison →
          </Link>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="border-t border-b border-border bg-card py-16 sm:py-20 px-4 sm:px-6 text-center">
        <h2 className="text-3xl font-bold text-foreground mb-4">
          Start your novel today -<br className="hidden sm:block" /> free, no CC required
        </h2>
        <p className="text-muted-foreground mb-8">Your AI co-author is ready. Your story is waiting.</p>
        <button
          onClick={handlePrimaryCta}
          className="inline-block px-8 py-3 bg-primary text-primary-foreground font-semibold rounded-md hover:bg-primary/90 transition-colors text-lg"
        >
          Start Writing Free
        </button>
        <p className="text-sm text-muted-foreground mt-4">No credit card required · Your story, your rights · Cancel anytime</p>
      </section>

      <Footer />
    </div>
  );
}
